import { LayerKey } from '@festina-life/api'
import { IconButton, NavLink } from '@festina-life/ui/actions'
import { Icon, Stack } from '@festina-life/ui/structure'
import cx from 'classnames'
import type { MouseEvent } from 'react'

import { configurationItem, scratchpad } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useOperationsContext } from '../../workspace/operationsContext'
import { BlockIndicator } from '../blockIndicator/BlockIndicator'
import { ConfigurationItemIcon } from '../configurationItemIcon/ConfigurationItemIcon'
import { LayerIndicator } from '../layerIndicator/LayerIndicator'
import type { TabLink } from './types'

type Props = {
  link: TabLink
  onClose: () => void
}

export const NavigationTab = ({ link, onClose }: Props) => {
  const { server, branch } = useNavigationParams()
  const { workspaceKey } = useOperationsContext()

  const linkPath =
    link.variant === 'scratchpad'
      ? scratchpad({
          server,
          branch,
          block: link.block,
          layer: new LayerKey(link.layer),
          workspaceName: workspaceKey?.name,
        })
      : configurationItem({
          server,
          layer: new LayerKey(link.layer),
          branch,
          block: link.block,
          itemId: link.id,
          type: link.variant,
          workspaceName: workspaceKey?.name,
        })

  const handleClose = (event: MouseEvent) => {
    event.preventDefault()
    onClose()
  }

  const isAnnulOperation = link.variant === 'annulOperation'

  return (
    <NavLink
      to={linkPath}
      className={cx(
        'group rounded-none! border-b-2 px-1! pt-1! pb-0.5!',
        link.isActive
          ? 'bg-primary-500/25! border-primary-500'
          : 'border-transparent hover:border-primary-500/50',
      )}
      buttonVariant="discreet"
    >
      <Stack
        className={cx('items-center', {
          'text-draft-600! dark:text-draft-400!': link.isDraft,
        })}
      >
        {link.variant === 'scratchpad' ? (
          <Icon name="CommandLine" size="md" color="unset" />
        ) : (
          <ConfigurationItemIcon
            type={
              link.variant === 'annulOperation'
                ? (link.annulledEntityVariant ?? 'annulOperation')
                : link.variant
            }
          />
        )}
        <Stack className="items-center" gap="condensed">
          <span
            className={cx({
              'line-through': isAnnulOperation,
            })}
          >
            {link.label ?? link.id}
          </span>
          <BlockIndicator
            block={link.block}
            className="p-0! text-xs opacity-50"
            shouldShowIcon={false}
            shouldHideRootBlock
            isSecondaryBackground={false}
          />
          <LayerIndicator
            layer={new LayerKey(link.layer)}
            isSecondaryBackground={false}
            className="p-0! text-xs opacity-50"
          />
        </Stack>
      </Stack>
      <IconButton
        className={cx(
          'ms-0.5 -me-0.5 p-0.5!',
          !link.isActive && 'not-group-hover:invisible',
        )}
        onClick={handleClose}
        iconName="XMark"
        iconSize="sm"
      />
    </NavLink>
  )
}
